'use client';

import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useNavigationStore } from '@/hooks/useNavigationStore';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { loadAllLocations } from '@/lib/locationData';

const SearchPanel = dynamic(() => import('@/components/SearchPanel'), { ssr: false });
const QRPanel = dynamic(() => import('@/components/QRPanel'), { ssr: false });
import { 
  Compass, 
  HelpCircle, 
  Navigation, 
  MapPin, 
  X, 
  ArrowUpRight, 
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Check, 
  AlertTriangle, 
  Sparkles, 
  Footprints, 
  ArrowUpDown, 
  LogIn,
  WifiOff,
  RefreshCw,
  Building2,
  ChevronRight,
  Play,
  Pause,
  Share2
} from 'lucide-react';

const MapComponent = dynamic(() => import('@/components/Map'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-full relative bg-[#f0ede8]">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
          <span className="text-sm font-medium text-slate-500 tracking-wide">Loading campus map…</span>
        </div>
      </div>
    </div>
  )
});

const BUILDING_TARGETS: Record<string, { spaceId: string, floorId: string, locId: string }> = {
  'AB1': { spaceId: 's_4f40ac882fe3a889', floorId: 'm_8740f39750314ebd', locId: 'loc_68a472959cffcd29' },
  'AB2': { spaceId: 's_b26a69ef28e0e737', floorId: 'm_036743853a014dd2', locId: 'loc_entrance_ab2' },
  'AB3': { spaceId: 's_63d38be0872dd520', floorId: 'm_fa5a3ee91d369d98', locId: 'loc_entrance_ab3' },
  'AB4': { spaceId: 's_e32ec2610a0c4f97', floorId: 'm_eed3e5d1ce8cd49e', locId: 'loc_entrance_ab4' },
  'AB5': { spaceId: 's_6c46159f8ead4d93', floorId: 'm_2ae1c9d52d5a9470', locId: 'loc_82b596a8116810ec' },
};

export default function Home() {
  const {
    data,
    route,
    highlighted,
    extStart,
    fromId,
    toId,
    currentFloorId,
    error,
    selectedGateId,
    closedGates,
    multiFloorRoutes,
    steps,
    isLoading,
    isOffline,
    activeBuilding,
    isFetchingBuilding,
    loadOutsideCampus,
    enterBuilding,
    exitBuilding,
    handleNavigate,
    handleClear,
    handleGateToggle,
    handleFloorChange,
    handleSpaceClick,
    isSimulating,
    isSimPaused,
    simSpeed,
    simStepIndex,
    startSimulation,
    stopSimulation,
    setSimPaused,
    setSimSpeed,
    setSimStepIndex,
    setError
  } = useNavigationStore();

  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQRPanel, setShowQRPanel] = useState(false);
  const [qfBuilding, setQfBuilding] = useState('AB4');
  const [qfRoom, setQfRoom] = useState('');
  const [consolidatedLocations, setConsolidatedLocations] = useState<any[]>([]);

  useEffect(() => {
    // Defer search data loading until after map is interactive
    const timer = setTimeout(() => {
      loadAllLocations().then(allLocs => {
        const list: any[] = [
          { id: 'b_AB1', name: 'Academic Block 1 (AB1)', buildingId: 'AB1', spaceId: '', floorId: '' },
          { id: 'b_AB2', name: 'Academic Block 2 (AB2)', buildingId: 'AB2', spaceId: '', floorId: '' },
          { id: 'b_AB3', name: 'Academic Block 3 (AB3)', buildingId: 'AB3', spaceId: '', floorId: '' },
          { id: 'b_AB4', name: 'Academic Block 4 (AB4)', buildingId: 'AB4', spaceId: '', floorId: '' },
          { id: 'b_AB5', name: 'Academic Block 5 (AB5)', buildingId: 'AB5', spaceId: '', floorId: '' },
        ];
        for (const [bid, rawLocs] of Object.entries(allLocs)) {
          const buildingId = bid.toUpperCase();
          for (const loc of rawLocs) {
            const name = loc.name?.trim();
            if (!name) continue;
            if (/ahu|electric|ups|shaft|duct/i.test(name)) continue;
            let displayName = name;
            if (displayName.toLowerCase() === 'lift') displayName = 'Elevator & Stairs';
            list.push({
              id: `room_${buildingId}_${loc.id}`,
              name: `${displayName} (${buildingId})`,
              spaceId: loc.spaces?.[0]?.id || '',
              floorId: '',
              buildingId,
              type: 'room',
            });
          }
        }
        setConsolidatedLocations(list);
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleSearchNavigate = useCallback(async (fromId: string, toId: string) => {
    if (toId.startsWith('b_')) {
      const buildingId = toId.substring(2).toUpperCase();
      await enterBuilding(buildingId as any);
      const target = BUILDING_TARGETS[buildingId];
      if (target) {
        setTimeout(() => {
          handleNavigate('EXTERNAL', target.spaceId);
        }, 400);
      }
    } else if (toId.startsWith('room_')) {
      const parts = toId.split('_');
      const buildingId = parts[1].toUpperCase();
      const originalLocId = parts.slice(2).join('_');
      
      if (activeBuilding?.toUpperCase() !== buildingId) {
        await enterBuilding(buildingId as any);
        setTimeout(() => {
          handleNavigate('EXTERNAL', originalLocId);
        }, 400);
      } else {
        handleNavigate('EXTERNAL', originalLocId);
      }
    } else {
      handleNavigate(fromId, toId);
    }
  }, [activeBuilding, enterBuilding, handleNavigate]);

  const handleShareLink = useCallback(() => {
    if (typeof window === 'undefined') return;

    const getCleanName = (idVal: string | undefined) => {
      if (!idVal) return null;
      if (idVal.toUpperCase() === 'EXTERNAL') return 'EXTERNAL';
      if (data) {
        const found = data.locations.find(l => l.id === idVal);
        if (found) {
          const name = found.name;
          const parenIdx = name.lastIndexOf(' (');
          if (parenIdx > 0 && name.endsWith(')')) {
            return name.substring(0, parenIdx);
          }
          return name;
        }
      }
      return idVal;
    };

    const cleanTo = getCleanName(toId);
    const building = activeBuilding?.toLowerCase();

    // If we have an active destination, show QR panel instead of just copying
    if (building && cleanTo && cleanTo !== 'EXTERNAL') {
      setShowQRPanel(true);
      return;
    }

    const origin = window.location.origin;
    const shareUrl = building ? `${origin}/map/${building}` : origin;
    const shareTitle = `MIT Manipal Indoor Map — ${activeBuilding?.toUpperCase() || 'Campus'}`;
    const shareText = 'Interactive indoor map of MIT Manipal campus';

    if (typeof navigator.share === 'function' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      navigator.share({ title: shareTitle, text: shareText, url: shareUrl }).catch(() => {
        navigator.clipboard.writeText(shareUrl).catch(() => {});
      });
    } else {
      navigator.clipboard.writeText(shareUrl).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  }, [toId, activeBuilding, data]);


  const parsedUrlParams = useRef<string | null>(null);
  const isInitializedRef = useRef(false);

  // Parse shareable links / incoming URL query parameters
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isInitializedRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const buildingParam = params.get('building');

    if (buildingParam) {
      // Store initial search string NOW (before enterBuilding triggers re-renders)
      if (!parsedUrlParams.current) {
        parsedUrlParams.current = window.location.search;
      }
      const buildingCode = buildingParam.toUpperCase();
      if (activeBuilding?.toUpperCase() !== buildingCode && !isFetchingBuilding) {
        enterBuilding(buildingCode as any);
        return;
      }
    } else {
      isInitializedRef.current = true;
    }

    // Once the data is loaded and active building matches, auto-highlight or navigate
    const initialSearch = parsedUrlParams.current ?? window.location.search;
    if (!parsedUrlParams.current) parsedUrlParams.current = initialSearch;
    const guardKey = `${activeBuilding}_${initialSearch}`;
    if (data && activeBuilding && parsedUrlParams.current === initialSearch) {
      parsedUrlParams.current = guardKey; // Mark as processed so we don't fire again

      // Parse from the stored initial URL (not the current one which may have been mutated)
      const initialParams = new URLSearchParams(initialSearch);
      const roomVal = initialParams.get('room');
      const fromVal = initialParams.get('from');
      const toVal = initialParams.get('to');

      /**
       * findLocation — robust multi-strategy matcher:
       * 1. Exact ID / spaceId match
       * 2. Exact name match (case-insensitive)
       * 3. Name stripped of floor disambiguation suffix match
       * 4. Prefix/starts-with match (handles "304" matching "304 (Third Floor)")
       * 5. Numeric substring — e.g. "304" found in "Room 304"
       */
      const findLocation = (val: string) => {
        const v = val.toLowerCase().trim();
        return (
          data.locations.find(l => l.id === val || l.spaceId === val) ??
          data.locations.find(l => l.name.toLowerCase() === v) ??
          data.locations.find(l => l.name.toLowerCase().split('(')[0].trim() === v) ??
          data.locations.find(l => l.name.toLowerCase().startsWith(v)) ??
          data.locations.find(l => l.name.toLowerCase().replace(/\s+/g, ' ').includes(v))
        );
      };

      const resolveAndNavigate = (toRaw: string, fromRaw?: string) => {
        const toLoc = toRaw.toUpperCase() === 'EXTERNAL' ? { id: 'EXTERNAL' } : findLocation(toRaw);
        if (!toLoc) {
          // Surface a clear error instead of silently doing nothing
          setError(`Room "${toRaw}" not found in ${activeBuilding?.toUpperCase()}. Try searching manually.`);
          isInitializedRef.current = true;
          return;
        }
        const fromLoc = fromRaw && fromRaw.toUpperCase() !== 'EXTERNAL'
          ? findLocation(fromRaw)
          : { id: 'EXTERNAL' };
        handleNavigate((fromLoc ?? { id: 'EXTERNAL' }).id, toLoc.id);
      };

      if (toVal && fromVal) {
        resolveAndNavigate(toVal, fromVal);
      } else if (toVal) {
        resolveAndNavigate(toVal);
      } else if (roomVal) {
        resolveAndNavigate(roomVal);
      }
      isInitializedRef.current = true;
    }
  }, [activeBuilding, data, enterBuilding, handleNavigate, handleSpaceClick, setError]);

  // Synchronize state changes to URL search params for instant copy-paste sharing
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isInitializedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    let changed = false;

    const getCleanVal = (idVal: string) => {
      if (idVal.toUpperCase() === 'EXTERNAL') return 'EXTERNAL';
      if (data) {
        const found = data.locations.find(l => l.id === idVal);
        if (found) {
          // Strip floor-disambiguation suffix like " (Ground Floor)" for clean URLs
          // Numeric room names (e.g. "104", "204") are kept as-is
          const name = found.name;
          const parenIdx = name.lastIndexOf(' (');
          if (parenIdx > 0 && name.endsWith(')')) {
            return name.substring(0, parenIdx);
          }
          return name;
        }
      }
      return idVal;
    };

    if (activeBuilding) {
      if (params.get('building') !== activeBuilding) {
        params.set('building', activeBuilding);
        changed = true;
      }
    } else {
      if (params.has('building')) {
        params.delete('building');
        changed = true;
      }
    }

    const cleanToVal = toId ? getCleanVal(toId) : null;
    const cleanFromVal = fromId ? getCleanVal(fromId) : null;

    if (cleanToVal) {
      if (params.get('to') !== cleanToVal) {
        params.set('to', cleanToVal);
        changed = true;
      }
      if (params.has('room')) {
        params.delete('room');
        changed = true;
      }
    } else {
      if (params.has('to')) {
        params.delete('to');
        changed = true;
      }
    }

    if (cleanFromVal && !cleanToVal) {
      if (params.get('room') !== cleanFromVal) {
        params.set('room', cleanFromVal);
        changed = true;
      }
    } else {
      if (params.has('room')) {
        params.delete('room');
        changed = true;
      }
    }

    if (cleanFromVal && cleanToVal) {
      if (params.get('from') !== cleanFromVal) {
        params.set('from', cleanFromVal);
        changed = true;
      }
    } else {
      if (params.has('from')) {
        params.delete('from');
        changed = true;
      }
    }

    if (changed) {
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({ ...window.history.state }, '', newUrl);
    }
  }, [activeBuilding, fromId, toId, data]);

  // Monitor network status & load outside style configuration
  useEffect(() => {
    loadOutsideCampus();

    if (typeof window !== 'undefined') {
      const handleOnline = () => loadOutsideCampus();
      window.addEventListener('online', handleOnline);
      return () => {
        window.removeEventListener('online', handleOnline);
      };
    }
  }, [loadOutsideCampus]);

  const destLocation = data?.locations.find(l => l.id === toId);

  // Helper to render step icons without cursors (touch safe)
  const getStepIcon = (iconName: string, isActive: boolean) => {
    const baseClass = `w-5 h-5 ${isActive ? 'text-white' : 'text-slate-500'}`;
    switch (iconName) {
      case 'door':
        return <LogIn className={baseClass} />;
      case 'walk':
        return <Footprints className={baseClass} />;
      case 'elevator':
        return <ArrowUpDown className={baseClass} />;
      case 'pin':
        return <MapPin className={baseClass} />;
      default:
        return <Check className={baseClass} />;
    }
  };

  return (
    <main className="relative w-screen h-screen overflow-hidden bg-slate-50 font-sans text-slate-900 select-none pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
      
      {/* Premium ambient glowing blobs behind overlays (Monochrome gray blurs) */}
      <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] max-w-[600px] max-h-[600px] rounded-full bg-slate-300/5 blur-[120px] pointer-events-none z-[20]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[45vw] h-[45vw] max-w-[500px] max-h-[500px] rounded-full bg-slate-400/5 blur-[100px] pointer-events-none z-[20]" />

      {/* Offline / Slow Network Banner */}
      {isOffline && (
        <div className="absolute top-0 left-0 right-0 z-[260] bg-amber-500 text-white px-4 py-2 text-center text-xs font-bold flex items-center justify-center gap-2 shadow-md">
          <WifiOff className="w-4 h-4 animate-pulse shrink-0" />
          <span>No internet — using cached maps. Navigation still works.</span>
          <button
            onClick={() => window.location.reload()}
            className="ml-2 underline underline-offset-2 opacity-80 hover:opacity-100"
          >
            Retry
          </button>
        </div>
      )}

      {/* Lazy loading transition spinner (Rule 1) */}
      {isFetchingBuilding && (
        <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md z-[300] flex flex-col items-center justify-center gap-4 text-white animate-in fade-in duration-200">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin shrink-0" />
          <p className="text-sm font-extrabold tracking-widest uppercase">Loading Indoor Maps...</p>
        </div>
      )}

      {/* Floating Exit Building & Share Buttons */}
      {activeBuilding && !isSimulating && (
        <div className="absolute mobile-safe-exit left-4 md:left-[412px] z-[230] flex gap-2">
          <button
            onClick={exitBuilding}
            className="h-11 px-5 bg-white/95 border border-slate-200/80 text-slate-700 font-bold rounded-full text-xs flex items-center justify-center gap-1.5 shadow-md active:scale-95 transition-all backdrop-blur-md"
          >
            <Compass className="w-4 h-4 text-slate-500" />
            <span className="hidden sm:inline">Exit Indoor view</span>
            <span className="inline sm:hidden">Exit</span>
          </button>
          
          <button
            onClick={handleShareLink}
            className="h-11 px-4 md:px-5 bg-slate-900 hover:bg-black text-white font-bold rounded-full text-xs flex items-center justify-center gap-1.5 shadow-md active:scale-95 transition-all"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-emerald-400 animate-in fade-in zoom-in duration-100" />
                <span className="hidden sm:inline">Link Copied!</span>
                <span className="inline sm:hidden">Copied!</span>
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4 text-slate-300" />
                <span className="hidden sm:inline">{toId ? 'Share + QR' : 'Share Map'}</span>
                <span className="inline sm:hidden">Share</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Background/Watermark UI */}
      <div className="absolute top-6 right-6 z-[50] pointer-events-none opacity-90 hidden md:flex items-center gap-2">
        <div className="px-4 py-2 bg-white/95 border border-slate-200/80 text-slate-700 rounded-full text-xs font-bold flex items-center gap-2 shadow-sm backdrop-blur-md">
          <Sparkles className="w-4 h-4 text-slate-500 animate-pulse" /> 
          <span>{activeBuilding ? `MIT Campus - ${activeBuilding.toUpperCase()}` : 'MIT Campus'}</span>
        </div>
      </div>

      {error && !data && !activeBuilding ? (
        <div className="flex flex-col items-center justify-center h-full gap-4 text-slate-500 bg-slate-50 p-6 text-center">
          <AlertTriangle className="w-16 h-16 text-amber-500 shrink-0" />
          <div className="text-2xl font-extrabold tracking-tight text-slate-900">Connection Failed</div>
          <div className="font-semibold text-sm text-slate-600 max-w-xs">{error}</div>
          <button
            onClick={() => loadOutsideCampus()}
            className="flex items-center gap-2 px-6 py-3 bg-slate-900 active:scale-95 active:bg-black text-white font-bold rounded-2xl shadow-md transition-all h-11 shrink-0"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Retry Connection</span>
          </button>
        </div>
      ) : (
        <>
          {!isSimulating && (
            <SearchPanel 
              locations={consolidatedLocations} 
              onNavigate={handleSearchNavigate}
              onClear={handleClear}
              selectedFromId={fromId}
              selectedToId={toId}
              isNavigating={!!route}
            />
          )}

          {/* Quick-Find Landing Card — shown on fresh load with no building selected */}
          {!activeBuilding && !isFetchingBuilding && !isLoading && (
            <QuickFindCard
              selectedBuilding={qfBuilding}
              onBuildingChange={setQfBuilding}
              room={qfRoom}
              onRoomChange={setQfRoom}
              onEnterBuilding={enterBuilding}
            />
          )}

          {/* Wrap MapComponent inside our production WebGL Error Boundary */}
          <ErrorBoundary>
            <MapComponent 
              key={activeBuilding || 'outside'}
              route={route} 
              highlightedSpaces={highlighted} 
              onSpaceClick={handleSpaceClick}
              externalStart={extStart}
              currentFloorId={currentFloorId}
              onFloorChange={handleFloorChange}
              destinationName={destLocation?.name}
              closedGates={
                activeBuilding === 'ab4' || activeBuilding === 'ab1' || activeBuilding === 'ab5' || activeBuilding === 'ab2'
                  ? closedGates
                  : (currentFloorId !== 'm_fa5a3ee91d369d98'
                      ? Array.from(new Set([...closedGates, 'side_a', 'side_b']))
                      : closedGates)
              }
              onGateToggle={handleGateToggle}
              isIndoor={!!activeBuilding}
              activeBuilding={activeBuilding || undefined}
              isSimulating={isSimulating}
              isSimPaused={isSimPaused}
              simSpeed={simSpeed}
              simStepIndex={simStepIndex}
              onSimStepIndexChange={setSimStepIndex}
              onSimStop={stopSimulation}
            />
          </ErrorBoundary>
        </>
      )}


      {/* Floating turn-by-turn Navigation Guidance Panel (DESKTOP) */}
      {route && destLocation && !isSimulating && (
        <div className="absolute hidden md:block md:bottom-auto md:left-6 md:top-[88px] z-[240] md:w-[380px] bg-white/98 backdrop-blur-2xl p-6 rounded-[28px] border border-slate-200/80 shadow-[0_20px_50px_rgba(0,0,0,0.12)] font-sans animate-in slide-in-from-bottom duration-300 transition-all ease-in-out">
          {/* Header */}
          <div className="flex items-start justify-between border-b border-slate-100 pb-4 mb-4">
            <div className="flex-1 min-w-0 pr-2">
              <div className="text-xs font-bold text-slate-500 flex items-center gap-1.5 uppercase tracking-wide">
                <Navigation className="w-3.5 h-3.5 text-slate-400 animate-pulse" /> Directions
              </div>
              <h3 className="text-lg font-extrabold text-slate-900 mt-1 truncate">
                To <span className="text-slate-900 font-black">{destLocation.name.split('(')[0].trim()}</span>
              </h3>
              <p className="text-xs font-medium text-slate-500 mt-1 flex items-center gap-1.5">
                <span>{multiFloorRoutes ? 'Multi-Floor Route' : 'Same-Level Path'}</span>
                <span className="w-1 h-1 bg-slate-300 rounded-full" />
                <span className="text-emerald-600 font-semibold">Helpful walkthrough</span>
              </p>
            </div>
            
            <button 
              onClick={handleClear}
              className="w-11 h-11 flex items-center justify-center bg-slate-50 active:scale-95 active:bg-slate-200 rounded-full text-slate-500 transition-all shrink-0"
              title="End Navigation"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Stepper Timeline (Landmark-based Turn-by-Turn) */}
          <div className="space-y-6 max-h-[300px] overflow-y-auto pr-1 scrollbar-thin">
            {steps
              .map((step, idx) => {
                const isLast = idx === steps.length - 1;
                return (
                  <div 
                    key={idx} 
                    className="flex gap-4 p-3 rounded-2xl transition-all"
                  >
                  <div className="flex flex-col items-center shrink-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      step.isCompleted
                        ? 'bg-slate-800 text-white'
                        : step.isActive
                        ? 'bg-slate-950 text-white shadow-md shadow-slate-950/20'
                        : 'bg-slate-50 text-slate-400 border border-slate-200'
                    }`}>
                      {step.isCompleted ? (
                        <Check className="w-4.5 h-4.5 stroke-[3]" />
                      ) : (
                        getStepIcon(step.icon, step.isActive)
                      )}
                    </div>
                    {!isLast && (
                      <div className={`w-0.5 h-12 my-1 transition-colors duration-300 ${
                        step.isCompleted ? 'bg-slate-800' : 'bg-slate-200'
                      }`} />
                    )}
                  </div>

                  <div className="flex-1 pb-1">
                    <span className={`text-[11px] font-bold uppercase tracking-wider block ${
                      step.isActive ? 'text-slate-800' : 'text-slate-450'
                    }`}>
                      Step {idx + 1}
                    </span>
                    <span className={`text-sm font-semibold mt-1 block leading-relaxed ${
                      step.isActive ? 'text-slate-950 font-black' : 'text-slate-550'
                    }`}>
                      {step.description}
                    </span>

                    {/* Floor switcher button (44px touch friendly height - Rule 3) */}
                    {step.action && (
                      <button
                        onClick={() => handleFloorChange(step.action!.targetFloorId)}
                        className="mt-3 h-11 px-5 bg-slate-900 hover:bg-black text-white rounded-2xl text-xs font-bold shadow-md shadow-slate-900/15 active:scale-95 transition-transform flex items-center justify-center gap-1.5 border border-slate-850 group"
                      >
                        {step.action.label}
                        <ArrowUpRight className="w-4 h-4 transition-transform group-active:translate-x-0.5 group-active:-translate-y-0.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* End Navigation Footer */}
          <div className="mt-5 pt-4 border-t border-slate-150 flex items-center gap-3">
            <button
              onClick={startSimulation}
              className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white rounded-2xl text-xs font-black shadow-md shadow-emerald-600/10 transition-all flex items-center justify-center gap-1.5"
            >
              <Navigation className="w-4 h-4 fill-white" />
              Start Navigation
            </button>
            <button
              onClick={handleClear}
              className="h-11 px-4 bg-slate-100 hover:bg-slate-200 active:scale-95 text-slate-700 rounded-2xl text-xs font-bold transition-all"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Draggable & Collapsible Mobile Bottom Sheet (MOBILE) */}
      {route && destLocation && !isSimulating && (
        <div 
          className={`absolute bottom-0 left-0 right-0 z-[240] bg-white/98 backdrop-blur-2xl border-t border-slate-200/80 shadow-[0_-15px_40px_rgba(0,0,0,0.12)] p-6 pt-3 rounded-t-[32px] md:hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'h-[72vh]' : 'h-[35vh]'
          } flex flex-col pb-[env(safe-area-inset-bottom)]`}
        >
          {/* Visual Drag Handle Pill */}
          <div 
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-12 h-1.5 bg-slate-300 active:bg-slate-400 rounded-full mx-auto mb-4 cursor-pointer shrink-0 transition-colors"
          />

          {/* Header */}
          <div className="flex items-start justify-between border-b border-slate-100 pb-3 mb-4 shrink-0">
            <div className="flex-1 min-w-0 pr-2">
              <div className="text-[11px] font-bold text-slate-805 flex items-center gap-1.5 uppercase tracking-wide">
                <Navigation className="w-3.5 h-3.5 fill-slate-200" /> Natural Directions
              </div>
              <h3 className="text-base font-extrabold text-slate-900 mt-1 truncate">
                To <span className="text-slate-850">{destLocation.name.split('(')[0].trim()}</span>
              </h3>
              <p className="text-xs font-medium text-slate-500 mt-1 flex items-center gap-1.5">
                <span>{multiFloorRoutes ? 'Multi-Floor' : 'Same-Level'}</span>
                <span className="w-1 h-1 bg-slate-300 rounded-full" />
                <span className="text-slate-800 font-bold">Directions manual</span>
              </p>
            </div>
            
            <button 
              onClick={handleClear}
              className="w-11 h-11 flex items-center justify-center bg-slate-50 active:scale-95 active:bg-slate-200 rounded-full text-slate-500 transition-all shrink-0"
              title="End Navigation"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Steps (Scrollable Container) */}
          <div className="flex-1 overflow-y-auto pr-1 pb-4 scrollbar-thin">
            <div className="space-y-6">
              {steps
                .map((step, idx) => {
                  const isLast = idx === steps.length - 1;
                  return (
                    <div 
                      key={idx} 
                      className="flex gap-4 p-3 rounded-2xl transition-all"
                    >
                    <div className="flex flex-col items-center shrink-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                        step.isCompleted
                          ? 'bg-slate-800 text-white'
                          : step.isActive
                          ? 'bg-slate-950 text-white shadow-md shadow-slate-950/20'
                          : 'bg-slate-50 text-slate-400 border border-slate-200'
                      }`}>
                        {step.isCompleted ? (
                          <Check className="w-4.5 h-4.5 stroke-[3]" />
                        ) : (
                          getStepIcon(step.icon, step.isActive)
                        )}
                      </div>
                      {!isLast && (
                        <div className={`w-0.5 h-12 my-1 transition-colors duration-300 ${
                          step.isCompleted ? 'bg-slate-800' : 'bg-slate-200'
                        }`} />
                      )}
                    </div>

                    <div className="flex-1 pb-1">
                      <span className={`text-[10px] font-bold uppercase tracking-wider block ${
                        step.isActive ? 'text-slate-800' : 'text-slate-450'
                      }`}>
                        Step {idx + 1}
                      </span>
                      <span className={`text-sm font-semibold mt-0.5 block leading-relaxed ${
                        step.isActive ? 'text-slate-950 font-black' : 'text-slate-550'
                      }`}>
                        {step.description}
                      </span>

                      {/* Touch Friendly Floor Switcher Button */}
                      {step.action && (
                        <button
                          onClick={() => {
                            handleFloorChange(step.action!.targetFloorId);
                          }}
                          className="mt-3 h-11 px-5 bg-slate-900 hover:bg-black text-white rounded-2xl text-xs font-bold shadow-md shadow-slate-900/15 active:scale-95 transition-transform flex items-center justify-center gap-1.5 border border-slate-850"
                        >
                          {step.action.label}
                          <ArrowUpRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* End Navigation Footer */}
          <div className="pt-3 border-t border-slate-100 flex items-center gap-3 shrink-0">
            <button
              onClick={startSimulation}
              className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white rounded-2xl text-xs font-black shadow-md shadow-emerald-600/10 transition-all flex items-center justify-center gap-1.5"
            >
              <Navigation className="w-4 h-4 fill-white" />
              Start Navigation
            </button>
            <button
              onClick={handleClear}
              className="h-11 px-4 bg-slate-100 hover:bg-slate-200 active:scale-95 text-slate-700 rounded-2xl text-xs font-bold transition-all"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* 3D Google Maps Navigation HUD Overlays */}
      {isSimulating && destLocation && route && (
        <div className="absolute inset-0 pointer-events-none z-[300] flex flex-col justify-between p-4 md:p-6 font-sans">
          
          {/* Top Instruction Card */}
          <div className="w-full max-w-lg mx-auto bg-[#1a73e8] text-white rounded-3xl p-5 shadow-[0_20px_60px_rgba(0,0,0,0.3)] flex items-start gap-4 pointer-events-auto animate-in slide-in-from-top duration-300">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center shrink-0 shadow-lg">
              {(() => {
                const desc = (steps[simStepIndex]?.description || "").toLowerCase();
                if (desc.includes('left')) return <ArrowLeft className="w-6 h-6 text-white" />;
                if (desc.includes('right')) return <ArrowRight className="w-6 h-6 text-white" />;
                return <ArrowUp className="w-6 h-6 text-white" />;
              })()}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-blue-200">Walking Instructions</span>
              <h3 className="text-base font-extrabold text-white mt-0.5 leading-snug">
                {steps[simStepIndex]?.description || "Walk straight along the path"}
              </h3>
              <p className="text-xs text-blue-100 mt-1 font-semibold">
                Destination: {destLocation.name}
              </p>
            </div>
          </div>

          {/* Bottom Left Speedometer Card */}
          <div className="absolute left-4 bottom-32 md:left-6 md:bottom-36 pointer-events-auto animate-in fade-in zoom-in duration-300">
            <div className="w-20 h-20 bg-white border border-slate-200/80 rounded-2xl shadow-xl flex flex-col items-center justify-center font-sans">
              <span className="text-3xl font-black text-slate-950 tracking-tight leading-none">
                {isSimPaused ? '0' : '1.4'}
              </span>
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">
                {isSimPaused ? 'mph' : 'm/s'}
              </span>
            </div>
          </div>

          {/* Bottom HUD Controls Card */}
          <div className="w-full max-w-2xl mx-auto bg-white/95 backdrop-blur-xl text-slate-900 rounded-[32px] p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] md:pb-5 border border-slate-200/80 shadow-[0_20px_50px_rgba(0,0,0,0.15)] flex flex-col md:flex-row items-center justify-between gap-4 pointer-events-auto animate-in slide-in-from-bottom duration-300">
            
            {/* Left Column: Remaining stats */}
            <div className="flex items-center gap-4 w-full md:w-auto">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center shrink-0">
                <Footprints className="w-6 h-6 text-slate-700" />
              </div>
              <div>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-black tracking-tight text-slate-950">
                    {(() => {
                      const remCoords = route.slice(Math.min(simStepIndex * 2, route.length));
                      const meters = Math.ceil(getRouteDistanceMeters(remCoords.length > 0 ? remCoords : route));
                      return `${meters}m`;
                    })()}
                  </span>
                  <span className="text-slate-450 font-bold">•</span>
                  <span className="text-sm font-bold text-slate-500">
                    {(() => {
                      const remCoords = route.slice(Math.min(simStepIndex * 2, route.length));
                      const meters = getRouteDistanceMeters(remCoords.length > 0 ? remCoords : route);
                      const minutes = Math.ceil(meters / (1.4 * 60));
                      return `${minutes} min`;
                    })()}
                  </span>
                </div>
                <p className="text-[11px] font-bold text-emerald-600 uppercase tracking-wider mt-0.5">Smooth path ahead</p>
              </div>
            </div>

            {/* Middle: Controls */}
            <div className="flex items-center gap-3 w-full md:w-auto justify-center">
              {/* Play/Pause Button */}
              <button
                onClick={() => setSimPaused(!isSimPaused)}
                className={`w-12 h-12 rounded-full flex items-center justify-center active:scale-95 shadow-md transition-all ${
                  isSimPaused ? 'bg-slate-900 text-white hover:bg-black' : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                }`}
              >
                {isSimPaused ? <Play className="w-5 h-5 fill-current ml-0.5" /> : <Pause className="w-5 h-5 fill-current" />}
              </button>

              {/* Speed Multiplier Button */}
              {[1, 2, 5].map((speed) => (
                <button
                  key={speed}
                  onClick={() => setSimSpeed(speed)}
                  className={`px-4 h-10 rounded-2xl text-xs font-black tracking-wider transition-all active:scale-95 border ${
                    simSpeed === speed
                      ? 'bg-slate-950 text-white border-slate-950 shadow-sm'
                      : 'bg-white text-slate-655 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {speed}x
                </button>
              ))}
            </div>

            {/* Right: Exit */}
            <button
              onClick={stopSimulation}
              className="w-full md:w-auto h-12 px-6 bg-rose-600 hover:bg-rose-700 text-white font-extrabold rounded-2xl shadow-md shadow-rose-600/10 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              Exit Navigation
            </button>
          </div>

        </div>
      )}



      {/* QR Code Share Panel — shown when user taps Share with an active destination */}
      {showQRPanel && toId && activeBuilding && (() => {
        const getCleanName = (idVal: string) => {
          if (!data) return idVal;
          const found = data.locations.find(l => l.id === idVal);
          if (!found) return idVal;
          const name = found.name;
          const parenIdx = name.lastIndexOf(' (');
          return (parenIdx > 0 && name.endsWith(')')) ? name.substring(0, parenIdx) : name;
        };
        const cleanTo = getCleanName(toId);
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const shareUrl = `${origin}/map/${activeBuilding.toLowerCase()}/to/${encodeURIComponent(cleanTo)}`;
        const BUILDING_NAMES: Record<string, string> = {
          ab1: 'Academic Block 1', ab2: 'Academic Block 2', ab3: 'Academic Block 3',
          ab4: 'Academic Block 4', ab5: 'Academic Block 5',
        };
        return (
          <QRPanel
            url={shareUrl}
            roomName={cleanTo}
            buildingName={BUILDING_NAMES[activeBuilding.toLowerCase()] ?? activeBuilding.toUpperCase()}
            onClose={() => setShowQRPanel(false)}
          />
        );
      })()}

    </main>
  );
}

function QuickFindCard({
  selectedBuilding,
  onBuildingChange,
  room,
  onRoomChange,
  onEnterBuilding,
}: {
  selectedBuilding: string;
  onBuildingChange: (b: string) => void;
  room: string;
  onRoomChange: (r: string) => void;
  onEnterBuilding: (b: string) => void;
}) {
  const BUILDINGS = ['AB1', 'AB2', 'AB3', 'AB4', 'AB5'];

  const navigate = () => {
    if (!room.trim()) return;
    const url = `/map/${selectedBuilding.toLowerCase()}/to/${encodeURIComponent(room.trim())}`;
    window.location.href = url;
  };

  return (
    <div
      className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[200] w-[calc(100%-2rem)] max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <div className="bg-white/95 backdrop-blur-xl border border-slate-200/80 rounded-[28px] shadow-2xl shadow-slate-900/10 p-5 flex flex-col gap-4">
        {/* Section 1: Explore Blocks */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
              <svg className="w-3.5 h-3.5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-black text-slate-900 leading-none">Explore Indoor Blocks</p>
              <p className="text-[9px] text-slate-500 mt-0.5">Select a building block to open its 3D floor maps</p>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {BUILDINGS.map(b => (
              <button
                key={b}
                onClick={() => onEnterBuilding(b)}
                className="h-9 bg-slate-900 hover:bg-black text-white text-[11px] font-bold rounded-xl shadow-md transition-all active:scale-95 flex items-center justify-center"
              >
                {b}
              </button>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-slate-100 w-full" />

        {/* Section 2: Find a Room */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
              <svg className="w-3.5 h-3.5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-black text-slate-900 leading-none">Find a Room</p>
              <p className="text-[9px] text-slate-500 mt-0.5">Search and navigate directly to any room</p>
            </div>
          </div>

          {/* Building selector (to scope room search) */}
          <div className="flex gap-1 mb-2.5 bg-slate-50 p-0.5 rounded-lg border border-slate-100">
            {BUILDINGS.map(b => (
              <button
                key={b}
                onClick={() => onBuildingChange(b)}
                className={`flex-1 py-1 rounded-md text-[10px] font-bold transition-all ${
                  selectedBuilding === b
                    ? 'bg-white text-slate-900 shadow-sm border border-slate-200/50'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {b}
              </button>
            ))}
          </div>

          {/* Room input + navigate button */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Room number (e.g. 105, 307)…"
              value={room}
              onChange={e => onRoomChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') navigate(); }}
              className="flex-1 h-9 bg-slate-50 border border-slate-200/60 rounded-xl px-3 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1.5 focus:ring-slate-400 focus:border-transparent transition-all"
            />
            <button
              onClick={navigate}
              disabled={!room.trim()}
              aria-label="Navigate to room"
              className="h-9 w-9 bg-slate-900 hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl flex items-center justify-center shrink-0 shadow-md active:scale-95 transition-all"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function getRouteDistanceMeters(coords: [number, number][]): number {
  if (!coords || coords.length < 2) return 0;
  let dist = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const [lon1, lat1] = coords[i];
    const [lon2, lat2] = coords[i+1];
    const R = 6371e3; // metres
    const phi1 = lat1 * Math.PI/180;
    const phi2 = lat2 * Math.PI/180;
    const deltaPhi = (lat2-lat1) * Math.PI/180;
    const deltaLambda = (lon2-lon1) * Math.PI/180;

    const a = Math.sin(deltaPhi/2) * Math.sin(deltaPhi/2) +
              Math.cos(phi1) * Math.cos(phi2) *
              Math.sin(deltaLambda/2) * Math.sin(deltaLambda/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    dist += R * c;
  }
  return dist;
}
